import type { Symptom } from "@/components/site/SymptomGrid";
import type { Step } from "@/components/site/StepsList";
import type { BehaviorItem } from "@/components/site/BehaviorList";

export interface PageContent {
  slug: string;
  title: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  supporting: string;
  primaryCTA: { label: string; to: string };
  secondaryCTA: { label: string; to: string };
  trustBar?: string;
  symptomsEyebrow: string;
  symptomsTitle: string;
  symptomsIntro?: string;
  symptoms: Symptom[];
  impactTitle: string;
  impactIntro?: string;
  impactBullets: string[];
  evidence?: { quote: string; source: string; href?: string };
  approachEyebrow: string;
  approachTitle: string;
  approachSteps: Step[];
  behaviorsTitle: string;
  behaviors: BehaviorItem[];
  /** When set, this learning path replaces the behaviors section. */
  learningPathSlug?: string;
  /** YouTube (or other) embed URL. When set, the video section renders a real iframe. */
  videoEmbedUrl?: string;
  /** Optional portrait embed used only below the 768px breakpoint. */
  videoEmbedUrlMobile?: string;
  /** Optional override for the video section title. */
  videoTitle?: string;

  caseStudy?: {
    label: string;
    company: string;
    situation: string;
    action: string;
    result: string[];
  };
  finalTitle: string;
  finalBody: string;
}

// -----------------------------
// PROBLEM PAGES
// -----------------------------

export const problems: PageContent[] = [
  {
    slug: "preparing-new-supervisors",
    title: "New Supervisor Leadership Training | Leadership Edge Live",
    metaDescription:
      "Practical training for newly promoted supervisors. Online courses, 30-minutes sessions, that build trust, clear expectations, feedback, and delegation.",
    eyebrow: "Preparing new supervisors",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f53edca9b6578b189",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75c56c0515acab501f",
    headline: "You promoted your best worker. Nobody taught them to lead.",
    supporting:
      "Doing the work and running the people who do it are two different jobs. Most new supervisors get the title and no training.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "What this looks like",
    symptomsTitle: "The first six months go sideways",
    symptoms: [
      {
        title: "They still do the work themselves",
        body: "Just doing the work feels faster than teaching someone else. The team never learns and the supervisor never stops.",
      },
      {
        title: "Yesterday's peers are today's direct reports",
        body: "Nobody told them how to correct a friend. So they don't, and everyone notices who gets away with what.",
      },
      {
        title: "Expectations stay in the supervisor's head",
        body: "People get told what to do, never what good looks like. Then there are surprises at review time.",
      },
      {
        title: "Everything escalates to you",
        body: "Schedule conflicts, pay questions, two people who won't work together: Your supervisors bring problems instead of results.",
      },
    ],
    impactTitle: "What it costs you",
    impactBullets: [
      "Good people quit in their first year",
      "Your calendar fills with other people's decisions",
      "Performance problems surface months after they started",
      "Two teams, two completely different standards",
      "No bench strength when you need it",
    ],
    evidence: {
      quote:
        "50% of employees left a job at some point in their career to get away from their manager.",
      source: "Gallup study of 7,272 U.S. adults, State of the American Manager, 2015",
      href: "https://www.gallup.com/workplace/236570/managers-account-variance-employee-engagement.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "Build the habits before the crisis",
    approachSteps: [
      {
        title: "Find out who needs what",
        body: "A short assessment shows which supervisors struggle with conflict and which struggle with follow-through.",
      },
      {
        title: "Start with the basics",
        body: "Build trust, eliminate secret expectations, and give on-the-spot feedback.",
      },
      {
        title: "Practice the skills",
        body: "Your supervisors focus on actual problems and strengthen their skills.",
      },
      {
        title: "Build their development plan",
        body: "Build a comprehensive development plan focused on their key skill gaps instead of a random forgettable plan that targets no one.",
      },
    ],
    learningPathSlug: "smb-new-leader",
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Builds Trust",
        body: "Earn credibility with the crew you worked beside last month.",
        courses: "Creating a Culture of Trust · New Leader Boot Camp",
      },
      {
        name: "Communicates",
        body: "Say what good looks like before someone misses it.",
        courses: "Strengthening Teams through Communication · Assertiveness Skills",
      },
      {
        name: "Focuses on Results through People",
        body: "Hand real work over instead of doing it yourself at seven.",
        courses: "Delegating for Development · Performance Management",
      },
      {
        name: "Practices Self-Awareness",
        body: "Notice how you land on people before they stop telling you.",
        courses: "Leadership Essentials with Emotional Intelligence · Emotional Triggers",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 240-person specialty contractor",
      situation:
        "Promoted four foremen off the crew in one year. Each ran their jobsite differently, and the ops director settled every dispute between them.",
      action:
        "All four took New Leader Boot Camp, then Conquering Conflict. Their manager took Delegating for Development at the same time so the handoffs held.",
      result: [
        "Foremen handle crew disputes without the ops director",
        "Expectations set the same way on every jobsite",
        "Fewer first-year departures among new hires",
      ],
    },
    finalTitle: "Prepare them before the problems land on you",
    finalBody:
      "Tell us who you just promoted and what they're walking into. We'll point you at one course and one place to start.",
  },

  {
    slug: "creating-consistent-leadership",
    learningPathSlug: "smb-experienced-leader",
    title: "Create Consistent Leadership Across Teams | Leadership Edge Live",
    metaDescription:
      "Build one shared standard of leadership across managers, shifts, departments, and locations, without forcing every manager into the same style.",
    eyebrow: "Creating consistent leadership",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f53edca9b6578b18b",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75085713f348b07451",
    headline: "Your employees shouldn't need a different playbook for every manager.",
    supporting:
      "Same company, same policy, wildly different experience depending on who you report to. That's not personality. That's a gap nobody has closed.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "How it shows up",
    symptomsTitle: "Standards depend on the manager",
    symptoms: [
      {
        title: "The same behavior gets two answers",
        body: "One supervisor writes it up. Another lets it go. Employees figure out fast which team is easier.",
      },
      {
        title: "Direction changes as it travels down",
        body: "Leadership announces a priority. By the time it reaches the floor, three departments are chasing three different things.",
      },
      {
        title: "Some managers coach, most just assign",
        body: "Whether an employee grows this year comes down to luck of the org chart.",
      },
      {
        title: "HR keeps seeing the same case",
        body: "Names change, details change, but it's always communication, follow-through, or a conversation someone should have had sooner.",
      },
    ],
    impactTitle: "What inconsistency costs",
    impactBullets: [
      "Employees transfer to escape a manager",
      "Priorities mean something different in each department",
      "Accountability lands unevenly and people notice",
      "Change rolls out at four different speeds",
      "Everything depends on your few strong managers",
    ],
    evidence: {
      quote: "Managers account for 70% of the variance in their own team's engagement.",
      source: "Gallup, State of the American Manager, 2015",
      href: "https://news.gallup.com/businessjournal/182792/managers-account-variance-employee-engagement.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "A shared floor, not a script",
    approachSteps: [
      {
        title: "Agree on what good looks like",
        body: "Name the expectations every employee should understand, no matter whose team they land on.",
      },
      {
        title: "Align managers",
        body: "Same expectations, same language, locked in. Cross-department cohorts do most of the alignment work.",
      },
      {
        title: "Practice together",
        body: "Managers work the same conflict and the same feedback moment, then compare how they handled it.",
      },
      {
        title: "Keep the standard visible",
        body: "Revisit the expectations in one-on-ones so consistency always survives.",
      },
    ],
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Builds Trust",
        body: "Follow through the same way whether it's convenient or not.",
        courses: "Creating a Culture of Trust · Psychological Safety",
      },
      {
        name: "Communicates",
        body: "Same expectations, same words, whichever manager delivers them.",
        courses: "Strengthening Teams through Communication · Assertiveness Skills",
      },
      {
        name: "Matches Style to Needs",
        body: "Adjust how you lead each person without moving the standard.",
        courses: "Adapting Your Leadership Style · 4P Personality Styles",
      },
      {
        name: "Champions Change",
        body: "Carry the same message your peers are carrying to their teams.",
        courses: "Leading Change · Turn Your Ideas into Results",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 400-person multi-site services company",
      situation:
        "Six locations, six general managers, six ways of handling attendance and performance. Employees compared notes across sites, and HR fielded the fallout every month.",
      action:
        "All six went through Creating a Culture of Trust and Performance Management in the same cohort, so they built one shared standard instead of six.",
      result: [
        "One attendance standard across every location",
        "Fewer manager disputes escalating to HR",
        "New managers inherit a standard, not a style",
      ],
    },
    finalTitle: "Give everyone the same standard of leadership",
    finalBody:
      "Tell us where the gaps between your managers show up. We'll help you find the two or three behaviors that would close them.",
  },

  {
    slug: "building-accountability",
    learningPathSlug: "smb-accountability",
    title: "Build Leadership Accountability Across Teams | Leadership Edge Live",
    metaDescription:
      "Help managers set clear expectations, follow through, and address gaps early, so accountability creates ownership instead of resentment.",
    eyebrow: "Building accountability",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f8c8476518efe12a1",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75a55cdea9c47fd3ee",
    headline: "Accountability isn't where you start. It's where you arrive.",
    supporting:
      "Skip straight to expectations and consequences, and all you build is resentment. The leaders who understand real accountability get the order right.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "How it shows up",
    symptomsTitle: "The problem starts before the performance does",
    symptoms: [
      {
        title: "Expectations were implied, never confirmed",
        body: "The manager assumed it was obvious. The employee assumed something else. Nobody checked until the deadline passed.",
      },
      {
        title: "Missed commitments pass without comment",
        body: "One manager addresses it that day. Another waits for the annual review and lists six months of examples.",
      },
      {
        title: "Managers just do it themselves",
        body: "Taking the work back protects this week's result but guarantees you'll do it again next week.",
      },
      {
        title: "Accountability gets confused with punishment",
        body: "Leaders skip the conversation because the only two options they see are confrontation and silence.",
      },
    ],
    impactTitle: "What weak accountability costs",
    impactBullets: [
      "Your best people carry the slack",
      "Standards drift a little every quarter",
      "Turnover among the people you'd keep",
      "Commitments to customers get missed quietly",
      "Senior leaders settle day-to-day performance issues",
    ],
    evidence: {
      quote:
        "52% of voluntarily exiting employees say their manager or organization could have done something to prevent them from leaving.",
      source: "Gallup, 2019",
      href: "https://www.gallup.com/workplace/247391/fixable-problem-costs-businesses-trillion.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "Relationship comes first",
    approachSteps: [
      {
        title: "Build the relationship",
        body: "When you lead from position instead of relationship, you pay for it in morale, commitment, and discretionary effort. Everything below depends on getting this first.",
      },
      {
        title: "Set the expectations",
        body: "Name the outcome, the owner, the deadline, and how you'll both know it worked. Delivered inside a relationship, this reads as clarity. Delivered without one, it reads as a list of accusations.",
      },
      {
        title: "Develop the skill",
        body: "Find out whether the gap is clarity, skill, time, or authority, then close it. Expecting something you never developed isn't accountability.",
      },
      {
        title: "Give the feedback",
        body: "A five-minute question on Wednesday beats a difficult conversation on Friday. Feedback is how expectations stay alive between the assignment and the deadline.",
      },
      {
        title: "Then hold them accountable",
        body: "Describe what happened, restate the standard, agree on the next step, and move on. Do the first four and this one stops being confrontation.",
      },
    ],
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Sets Goals",
        body: "Turn a vague priority into something you can actually check.",
        courses: "Driving Results through Goal Setting · Practical Project Management",
      },
      {
        name: "Communicates",
        body: "Confirm they heard you instead of treating quiet as agreement.",
        courses: "Strengthening Teams through Communication · Assertiveness Skills",
      },
      {
        name: "Focuses on Results through People",
        body: "Hand the work back with support instead of finishing it yourself.",
        courses: "Delegating for Development · Performance Management",
      },
      {
        name: "Demonstrates Self-Management",
        body: "Stay steady when the conversation gets uncomfortable.",
        courses: "Emotional Triggers · Managing Stress",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 300-person regional distributor",
      situation:
        "Two of eight branch managers addressed missed commitments the week they happened. The rest saved it for annual reviews, and their best people were leaving.",
      action:
        "All eight took Performance Management, then On-the-Spot Development Conversations, and brought one real employee situation to every session.",
      result: [
        "Feedback happens in days, not quarters",
        "Fewer surprises at annual review time",
        "Managers handle issues before HR hears them",
      ],
    },
    finalTitle: "Make accountability routine, not a last resort",
    finalBody:
      "Tell us where things are slipping. We'll help you find the manager behaviors that make expectations clear and fair before anyone falls short.",
  },

  {
    slug: "improving-difficult-conversations",
    learningPathSlug: "smb-communication-conflict",
    title: "Difficult Conversations Training for Managers | Leadership Edge Live",
    metaDescription:
      "Help managers raise feedback, performance, and conflict issues earlier, in plain words, before they harden into resignations or grievances.",
    eyebrow: "Improving difficult conversations",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f53edca9b6578b18a",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75085713f348b07452",
    headline: "The conversation you skip today becomes the problem everyone lives with later.",
    supporting:
      "Nobody enjoys difficult conversations. But the cost of waiting is a resignation, a grievance, or a team that quietly stops trusting each other.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "How it shows up",
    symptomsTitle: "Avoidance has a pattern",
    symptoms: [
      {
        title: "Feedback waits until the manager is frustrated",
        body: "By then it isn't coaching. It's a list, and the employee hears emotion; not logic and a path forward.",
      },
      {
        title: "The message gets softened into nothing",
        body: "The manager wanted to protect the relationship. The employee walked out unsure anything was wrong.",
      },
      {
        title: "Small issues become formal ones",
        body: "There's no middle setting between saying nothing and starting a documented performance conversation.",
      },
      {
        title: "The conflict moves sideways",
        body: "People talk about each other in the hallway instead of to each other in a room.",
      },
    ],
    impactTitle: "What avoidance costs",
    impactBullets: [
      "Problems arrive at HR fully hardened",
      "Good employees leave without management ever hearing why",
      "Two people stop speaking, work slows",
      "Managers dread one-on-ones and cancel them",
      "Small fixes turn into legal exposure",
    ],
    evidence: {
      quote:
        "51% of exiting employees say that in the three months before they left, no manager or leader spoke with them about their job satisfaction or future.",
      source: "Gallup, 2019",
      href: "https://www.gallup.com/workplace/247391/fixable-problem-costs-businesses-trillion.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "Facts, perspective, dialogue, needs",
    approachSteps: [
      {
        title: "Start with the facts",
        body: "Just what happened. No interpretation, no history, no adjectives. Facts are the only part of this you both already agree on.",
      },
      {
        title: "Add your perspective, tentatively",
        body: 'How you\'re reading those facts, said with language that leaves room to be wrong. Not "you did this to me" but more like "here\'s how this is landing on me."',
      },
      {
        title: "Open the door",
        body: "Stop talking and ask how they see it. Half the time the cause isn't what you assumed, and you only find that out if you leave space for it.",
      },
      {
        title: "Close with what you need",
        body: "Out loud, in plain words. I need. Most managers describe the problem and never once say what they actually want to happen next.",
      },
    ],
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Demonstrates Self-Management",
        body: "Stay useful when someone gets defensive or upset.",
        courses: "Emotional Triggers · Managing Stress",
      },
      {
        name: "Communicates",
        body: "Say the hard part clearly, then listen longer than feels natural.",
        courses: "Assertiveness Skills · Strengthening Teams through Communication",
      },
      {
        name: "Diagnoses Needs",
        body: "Ask what's causing it before deciding what to do.",
        courses: "Analytical Thinking Skills · Making Better Decisions",
      },
      {
        name: "Builds Trust",
        body: "Handle it in a way the person can respect afterward.",
        courses: "Creating a Culture of Trust · Psychological Safety",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 120-person professional services firm",
      situation:
        "Two senior team leads stopped speaking after a project handoff went badly. Their teams routed work around the conflict for eight months before anyone raised it.",
      action:
        "Both leads and their manager took Conquering Conflict, then On-the-Spot Development Conversations. Each brought a real, unresolved conversation to practice on.",
      result: [
        "The handoff conflict got named and settled",
        "Managers raise issues in weeks, not quarters",
        "Fewer disputes reaching the partner group",
      ],
    },
    finalTitle: "Move the hard conversations earlier",
    finalBody:
      "Tell us which conversations your managers keep putting off. We'll help you build the words and the confidence to have them sooner.",
  },

  {
    slug: "leading-through-change",
    title: "Change Leadership Development for Managers | Leadership Edge Live",
    metaDescription:
      "Get the middle layer ready before the announcement. Training that helps managers explain the reason, take the pushback, and keep a rollout alive.",
    eyebrow: "Leading through change",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f55aedeac6cf0d79a",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75c56c0515acab501e",
    headline: "Change imposed is change opposed.",
    supporting: "Change stalls unless leadership manages the change effectively.",
    learningPathSlug: "smb-managing-change",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "How it shows up",
    symptomsTitle: "Change stalls between the announcement and the floor",
    symptoms: [
      {
        title: "Managers talk about the change without describing the reason",
        body: "People hear what's changing and never hear why. So they assume the worst version.",
      },
      {
        title: "Leaders wait until they know everything",
        body: "Silence doesn't buy time. It gets filled with rumors that are harder to undo.",
      },
      {
        title: "Pushback gets labeled as attitude",
        body: "The person raising the workload problem is usually the person who spotted it first.",
      },
      {
        title: "Managers sell something they haven't accepted",
        body: "They have their own questions about the change and no place to ask them.",
      },
    ],
    impactTitle: "What a stalled rollout costs",
    impactBullets: [
      "The new process quietly reverts in 3 months",
      "Your best people update their resumes",
      "Every department adopts a different version",
      "Rumors outrun anything leadership actually said",
      "The next change gets even less buy-in",
    ],
    evidence: {
      quote:
        "Seven in ten U.S. workers reported disruptive change at their organization in the past year, and leaders and managers absorbed the most of it.",
      source: "Gallup, Disruptive Change Is Hitting Leaders and Managers Hardest, 2025",
      href: "https://www.gallup.com/workplace/645152/disruptive-change-hitting-leaders-managers-hardest.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "Get the middle layer ready first",
    approachSteps: [
      {
        title: "Brief managers before employees",
        body: "Give them the reasoning and the open questions a week before the announcement.",
      },
      {
        title: "Let them work through it",
        body: "Managers need somewhere to be skeptical before they're asked to sound confident.",
      },
      {
        title: "Practice the questions they'll get",
        body: 'Including the ones with no good answer yet. Rehearse saying "I don\'t know."',
      },
      {
        title: "Keep talking after week one",
        body: "Most rollouts die from silence in month two, not resistance in week one.",
      },
    ],
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Champions Change",
        body: "Explain the reason, take the pushback, keep showing up.",
        courses: "Leading Change · Turn Your Ideas into Results",
      },
      {
        name: "Communicates",
        body: "Translate the strategy deck into what changes Monday morning.",
        courses: "Strengthening Teams through Communication · Assertiveness Skills",
      },
      {
        name: "Builds Trust",
        body: "Say what you don't know yet instead of guessing.",
        courses: "Creating a Culture of Trust · Psychological Safety",
      },
      {
        name: "Demonstrates Self-Management",
        body: "Lead steadily while you're still uncertain yourself.",
        courses: "Emotional Triggers · Managing Stress",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 500-person healthcare services organization",
      situation:
        "Rolled out a new scheduling system across nine sites. Managers found out the same morning their teams did, so they had no answers for a week.",
      action:
        "Site managers took Leading Change ahead of the next rollout, and practiced explaining the reasoning and fielding the hardest questions before anything was announced.",
      result: [
        "Managers answered questions on day one",
        "The same explanation given at every site",
        "Fewer workarounds surviving into month three",
      ],
    },
    finalTitle: "Turn the announcement into adoption",
    finalBody:
      "Tell us what's changing and who has to carry it. We'll help you get those managers ready before the announcement goes out.",
  },

  {
    slug: "scaling-leadership-development-lean-hr",
    title: "Scalable Leadership Development for Lean HR Teams | Leadership Edge Live",
    metaDescription:
      "Leadership development for HR teams of one or two. We handle curriculum, live facilitation, logistics, and reporting. You pick the priorities.",
    eyebrow: "Scaling development with a lean HR team",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad0a12f8c8476518efe12a2",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad2ff75c56c0515acab501d",
    headline: "You're the whole HR department. You shouldn't have to build training too.",
    supporting:
      "You know which managers need help. What you don't have is a curriculum, a facilitator, and forty hours to build both.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    symptomsEyebrow: "How it shows up",
    symptomsTitle: "Development keeps losing to whatever is on fire",
    symptoms: [
      {
        title: "The same manager problem, different names",
        body: "You've handled this exact complaint four times this year with four different supervisors.",
      },
      {
        title: "You have content, not a program",
        body: "A video library nobody finishes and a workshop from two years ago. No path between them.",
      },
      {
        title: "Every request starts from zero",
        body: "Find a facilitator, build the deck, chase the calendar, figure out follow-up. Then do it again and again.",
      },
      {
        title: "We need training, but no one has time for it",
        body: "You are told that we cannot pull people out of the business, but we will not have much of a business left if we do not solve this.",
      },
    ],
    impactTitle: "What carrying it alone costs",
    impactBullets: [
      "Development slips another quarter, then another",
      "You spend evenings building slide decks",
      "New supervisors learn by trial and error",
      "Growth outruns your ability to staff it",
      "You become the bottleneck you were hired to remove",
    ],
    evidence: {
      quote:
        "Manager engagement fell from 30% to 27% in a single year, and managers account for most of the global drop in employee engagement.",
      source: "Gallup, State of the Global Workplace, 2026",
      href: "https://www.gallup.com/workplace/349484/state-of-the-global-workplace.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "We run it, you pick the priorities",
    approachSteps: [
      {
        title: "Start with your worst problem",
        body: "Not a course catalog. Tell us what keeps landing on your desk every week.",
      },
      {
        title: "We build the path",
        body: "Course sequence, cohorts, and schedule, matched to the managers you actually have.",
      },
      {
        title: "We run the sessions",
        body: "Live facilitators, weekly online micro-sessions. You don't have to teach anything.",
      },
      {
        title: "You get the reporting",
        body: "Attendance, application, and what changed, in a form you can forward.",
      },
    ],
    behaviorsTitle: "What you stop doing yourself",
    behaviors: [
      {
        name: "Curriculum",
        body: "Courses already built and mapped to sixteen leader behaviors.",
      },
      {
        name: "Facilitation",
        body: "Live instructors run every session. You don't stand at the front of the room.",
      },
      {
        name: "Logistics",
        body: "Registration, reminders, rescheduling, and chasing the people who skip.",
      },
      {
        name: "Reporting",
        body: "Reporting based on user progress and course completion, as well as engagement with the Dev Pros.",
      },
    ],
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 650-person manufacturer with one HR generalist",
      situation:
        "One generalist covering payroll, benefits, recruiting, and compliance for three plants. Forty supervisors had never had leadership training and nobody had time to build it.",
      action:
        "Supervisors were split into three cohorts running one course a quarter. Registration, facilitation, and reporting were handled outside HR, so the generalist only picked priorities.",
      result: [
        "All forty supervisors trained within a year",
        "HR spends hours on it, not weeks",
        "Leadership sees a report each quarter",
      ],
    },
    finalTitle: "Build leaders without building a department",
    finalBody:
      "Tell us how many managers you have and how little time you have to train them. We'll outline something you can actually run without hiring anyone.",
  },
];
